import {useState, useEffect, useRef, useCallback} from '@wordpress/element';
import {DEFAULT_BILLING_ADDRESS, DEFAULT_SHIPPING_ADDRESS, i18n} from "../../../utils";

export const useProcessPayment = (
    {
        title,
        onSubmit,
        isExpress,
        responseTypes,
        onPaymentSetup,
        paymentMethodId,
        activePaymentMethod,
    }) => {
    const [paymentData, updatePaymentData] = useState({});
    const currentPaymentData = useRef({});

    const setPaymentData = useCallback((value, submit = true) => {
        if (value === null || value === '') {
            updatePaymentData({});
        } else {
            updatePaymentData(prev => ({
                ...prev,
                ...value,
                submit
            }));
        }
    }, []);

    const clearPaymentData = useCallback(() => {
        updatePaymentData(null);
    }, []);

    useEffect(() => {
        currentPaymentData.current = paymentData;
    }, [paymentData]);

    // Auto-submit when payment data is set
    useEffect(() => {
        if (paymentData && paymentData.submit) {
            onSubmit();
        }
    }, [paymentData, onSubmit]);

    /**
     * Extract billing address from Google Pay payment data
     */
    const extractBillingAddress = useCallback((paymentMethodData) => {
        const billing = {};
        const info = paymentMethodData?.info;

        if (info?.billingAddress) {
            const address = info.billingAddress;
            billing.first_name = address.name?.split(' ')[0] || '';
            billing.last_name = address.name?.split(' ').slice(1).join(' ') || '';
            billing.address_1 = address.address1 || '';
            billing.address_2 = address.address2 || '';
            billing.city = address.locality || '';
            billing.state = address.administrativeArea || '';
            billing.postcode = address.postalCode || '';
            billing.country = address.countryCode || '';
            billing.phone = address.phoneNumber || '';
        }

        return billing;
    }, []);

    /**
     * Extract shipping address from Google Pay payment data
     */
    const extractShippingAddress = useCallback((shippingAddress) => {
        if (!shippingAddress) {
            return {};
        }

        const shipping = {};
        shipping.first_name = shippingAddress.name?.split(' ')[0] || '';
        shipping.last_name = shippingAddress.name?.split(' ').slice(1).join(' ') || '';
        shipping.address_1 = shippingAddress.address1 || '';
        shipping.address_2 = shippingAddress.address2 || '';
        shipping.city = shippingAddress.locality || '';
        shipping.state = shippingAddress.administrativeArea || '';
        shipping.postcode = shippingAddress.postalCode || '';
        shipping.country = shippingAddress.countryCode || '';

        if (shippingAddress.phoneNumber) {
            shipping.phone = shippingAddress.phoneNumber;
        }

        return shipping;
    }, []);

    // Handle payment setup for order submission
    useEffect(() => {
        if (activePaymentMethod === paymentMethodId) {
            const unsubscribe = onPaymentSetup(() => {
                const {orderId, paymentMethodData, email, shippingAddress, needsShipping} = currentPaymentData.current;

                if (!orderId) {
                    return {
                        type: responseTypes.ERROR,
                        message: sprintf(i18n.order_button_click, title)
                    }
                }
                const response = {
                    meta: {
                        paymentMethodData: {
                            ppcp_googlepay_paypal_order_id: orderId
                        },
                        ...(isExpress && {
                            billingAddress: {
                                ...DEFAULT_BILLING_ADDRESS,
                                ...extractBillingAddress(paymentMethodData),
                                ...(email && {email})
                            }
                        })
                    }
                };

                if (needsShipping && isExpress) {
                    response.meta.shippingAddress = {
                        ...DEFAULT_SHIPPING_ADDRESS,
                        ...extractShippingAddress(shippingAddress)
                    };
                }

                return {type: responseTypes.SUCCESS, ...response};
            });

            return () => unsubscribe();
        }
    }, [
        isExpress,
        onPaymentSetup,
        responseTypes,
        activePaymentMethod,
        paymentMethodId,
        extractBillingAddress,
        extractShippingAddress
    ]);

    return {
        paymentData,
        setPaymentData,
        clearPaymentData
    };
}